/**
 * Resource Performance Monitor (Simplified)
 *
 * Monitors and reports:
 * - Slow resources (>1s) or large resources (>500KB for assets, >50KB for API)
 * - Page load reports when load time >2s
 * - Core Web Vitals: LCP, INP (with exact actions), CLS (with pages) - via webVitalsService
 * - Network issues and connectivity strength - via webVitalsService
 *
 * Uses PerformanceMonitoringService for sending data
 * Batches resource metrics and pushes with debounce (2s delay)
 */

import PerformanceMonitoringService from '@/services/PerformanceMonitoringService'
import store from '@/store'
import { getCallerInfo, initCallStackTracker } from './callStackTracker'
import { getClientDiagnostics, type ClientDiagnostics } from './clientDiagnostics'
import { shouldReportResource, type ResourceSamplingInput } from './samplingService'
import { getCurrentSoftNavigationId } from './softNavigationHelper'
import { initWebVitalsService, getLatestLCP, getLatestINP, getLatestCLS, getNetworkData } from './webVitalsService'

const BATCH_DELAY_MS = 2000 // 2 second debounce

/** Get session ID from sessionStorage (consistent with promiseExecutorMonitoring) */
const getSessionId = (): string | undefined => {
  try {
    return sessionStorage.getItem('sessionId') || undefined
  } catch {
    return undefined
  }
}

/** Get user ID from store (consistent with promiseExecutorMonitoring) */
const getUserId = (): string | undefined => {
  try {
    return store.state.user?.user?.id
  } catch {
    return undefined
  }
}

const THRESHOLDS = {
  RESOURCE_DURATION_MS: 1000,
  RESOURCE_SIZE_ASSET_KB: 100,
  RESOURCE_SIZE_API_KB: 10,
  PAGE_LOAD_MS: 2000,
  FALLBACK_MS: 30000,
}

// ============================================================================
// Page Phase Tracking
// ============================================================================

type RequestPhase = 'initial-load' | 'navigation'

/** Track whether requests are during initial page load or after navigation */
let pagePhase: RequestPhase = 'initial-load'

/** Initialize phase tracking - switch to 'navigation' after page load */
function initPhaseTracking(): void {
  if (typeof window === 'undefined') return

  if (document.readyState === 'complete') {
    pagePhase = 'navigation'
  } else {
    window.addEventListener(
      'load',
      () => {
        pagePhase = 'navigation'
      },
      { once: true }
    )
  }
}

// ============================================================================
// Batch Collection
// ============================================================================

/** Sparse timing object - only includes non-zero values to reduce payload size */
interface ResourceTiming {
  redirect?: number // redirectEnd - redirectStart (omitted if 0)
  dns?: number // domainLookupEnd - domainLookupStart (omitted if 0)
  connect?: number // connectEnd - connectStart (omitted if 0)
  ssl?: number // connectEnd - secureConnectionStart (omitted if 0)
  ttfb?: number // responseStart - requestStart (omitted if 0)
  download?: number // responseEnd - responseStart (omitted if 0)
}

interface ResourceMetric {
  sessionId?: string // from sessionStorage, for correlating with promise executor logs
  userId?: string // from store, for correlating with user
  fullUrl: string // full URL of the resource
  type: string // initiatorType: fetch, xmlhttprequest, script, link, img, etc.
  httpMethod: string // GET, POST, PUT, DELETE, etc.
  caller: string // file:line that initiated the request
  callerStack: string // full call stack for debugging
  phase: RequestPhase // 'initial-load' or 'navigation'
  durationMs: number
  transferSizeKB: number // size over the wire (compressed)
  decodedSizeKB: number // response body size (uncompressed) - shortened from decodedBodySizeKB
  cached: boolean // true if served from browser cache (transferSize === 0 with content)
  timingMs: ResourceTiming // sparse object with only non-zero values
  httpStatus: number // HTTP status code (0 if unavailable), failed = httpStatus >= 400
  diagnostics?: ClientDiagnostics // client-side network/device info for diagnosing slow requests
  locationId: string | null
  url: string
  timestamp: number
  // Soft navigation correlation (Chrome 139+)
  softNavigationId?: number // Links resource to active soft navigation for correlation
  // Web vitals snapshot at time of resource capture
  lcp?: number
  inp?: number
  cls?: number
  effectiveNetworkType?: string
  networkStrength?: string
  // Device info
  browser?: string
  os?: string
}

/** Collected metrics waiting to be pushed */
let resourceBatch: ResourceMetric[] = []

/** Debounce timer for batch push */
let batchTimer: ReturnType<typeof setTimeout> | null = null

/** Push collected batch to backend */
const flushBatch = () => {
  if (resourceBatch.length === 0) return

  const payload = {
    resources: resourceBatch,
    timestamp: new Date().toISOString(),
  }

  // Clear batch before pushing
  resourceBatch = []

  PerformanceMonitoringService.logResourcePerformance(payload)
}

/** Add metrics to batch and schedule push */
const addToBatch = (metric: ResourceMetric) => {
  resourceBatch.push(metric)

  // Debounce: reset timer on each add, push after delay
  if (batchTimer) {
    clearTimeout(batchTimer)
  }
  batchTimer = setTimeout(flushBatch, BATCH_DELAY_MS)
}

// ============================================================================
// Resource Monitoring
// ============================================================================

// Extract location ID from URL
function getLocationId(): string | null {
  try {
    const match = window.location.pathname.match(/\/location\/([^/]+)/)
    return match ? match[1] : null
  } catch {
    return null
  }
}
// URLs to skip (our own monitoring endpoints + firestore socket data) - exported for testing
export const SKIP_URLS = [
  'resource-performance-logs',
  'page-load-performance-logs',
  'promise-executor-logs',
  'firestore.googleapis.com',
]

interface ReportCheck {
  shouldReport: boolean
  httpStatus: number
  isFailed: boolean
}

/** Check if resource should be reported using unified sampling service - exported for testing */
export function checkShouldReport(entry: PerformanceResourceTiming): ReportCheck {
  const httpStatus = (entry as any).responseStatus || 0 // Chrome 109+, Firefox 119+
  const isFailed = httpStatus >= 400

  // Use unified sampling service
  const samplingInput: ResourceSamplingInput = {
    url: entry.name,
    durationMs: entry.duration,
    httpStatus,
    transferSizeBytes: entry.transferSize || 0,
    initiatorType: entry.initiatorType,
  }

  const result = shouldReportResource(samplingInput)

  return {
    shouldReport: result.shouldReport,
    httpStatus,
    isFailed,
  }
}

/** Initialize resource monitoring */
function initResourceMonitor(): void {
  if (typeof window === 'undefined' || !window.PerformanceObserver) return

  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        const resource = entry as PerformanceResourceTiming
        const { shouldReport: report, httpStatus } = checkShouldReport(resource)

        if (!report) continue

        // Extract sparse timing breakdown (only non-zero values) to reduce payload size
        const timing: ResourceTiming = {}
        const redirect = Math.round(resource.redirectEnd - resource.redirectStart)
        const dns = Math.round(resource.domainLookupEnd - resource.domainLookupStart)
        const connect = Math.round(resource.connectEnd - resource.connectStart)
        const ssl =
          resource.secureConnectionStart > 0 ? Math.round(resource.connectEnd - resource.secureConnectionStart) : 0
        const ttfb = Math.round(resource.responseStart - resource.requestStart)
        const download = Math.round(resource.responseEnd - resource.responseStart)

        // Only include non-zero values
        if (redirect > 0) timing.redirect = redirect
        if (dns > 0) timing.dns = dns
        if (connect > 0) timing.connect = connect
        if (ssl > 0) timing.ssl = ssl
        if (ttfb > 0) timing.ttfb = ttfb
        if (download > 0) timing.download = download

        // Get caller info from intercepted fetch/XHR
        const callerInfo = getCallerInfo(resource.name)

        // Capture web vitals snapshot at time of resource recording
        const lcp = getLatestLCP()
        const inp = getLatestINP()
        const cls = getLatestCLS()
        const network = getNetworkData()

        // Reliable cache detection: transferSize === 0 with actual content means cached
        const transferSize = resource.transferSize || 0
        const decodedSize = resource.decodedBodySize || 0
        const cached = transferSize === 0 && decodedSize > 0

        addToBatch({
          sessionId: getSessionId(),
          userId: getUserId(),
          fullUrl: resource.name,
          type: resource.initiatorType,
          httpMethod: callerInfo?.httpMethod || 'GET',
          caller: callerInfo?.caller || '',
          callerStack: callerInfo?.callerStack || '',
          phase: pagePhase,
          durationMs: Math.round(resource.duration),
          transferSizeKB: Math.round(transferSize / 1024),
          decodedSizeKB: Math.round(decodedSize / 1024),
          cached,
          timingMs: timing,
          httpStatus,
          diagnostics: getClientDiagnostics(),
          locationId: getLocationId(),
          url: window.location.href,
          timestamp: new Date().toISOString(),
          // Soft navigation correlation (Chrome 139+)
          softNavigationId: getCurrentSoftNavigationId(),
          // Web vitals snapshot
          lcp: lcp?.lcp,
          inp: inp?.inp,
          cls: cls?.cumulativeValue,
          effectiveNetworkType: network.effectiveType,
          networkStrength: network.strength,
          // Device info
          browser: network.browser,
          os: network.os,
        })
      }
    })

    // buffered: true captures resources that loaded before observer started
    observer.observe({ entryTypes: ['resource'], buffered: true })

    // Flush on page unload
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushBatch()
      }
    })
  } catch {
    // Silently fail
  }
}

// ============================================================================
// Page Load Reporting
// ============================================================================

// Collect page load report data
function collectPageLoadReport(): object {
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
  const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming

  const slowResources = resources
    .filter(r => r.duration > THRESHOLDS.RESOURCE_DURATION_MS)
    .sort((a, b) => b.duration - a.duration)
    .slice(0, 10)
    .map(r => ({
      name: r.name.split('/').pop()?.split('?')[0],
      type: r.initiatorType,
      duration: Math.round(r.duration),
      size: Math.round((r.transferSize || 0) / 1024),
    }))

  const largeResources = resources
    .filter(r => {
      const isApi = r.initiatorType === 'fetch' || r.initiatorType === 'xmlhttprequest'
      const threshold = isApi ? THRESHOLDS.RESOURCE_SIZE_API_KB * 1024 : THRESHOLDS.RESOURCE_SIZE_ASSET_KB * 1024
      return (r.transferSize || 0) > threshold
    })
    .sort((a, b) => (b.transferSize || 0) - (a.transferSize || 0))
    .slice(0, 10)
    .map(r => ({
      name: r.name.split('/').pop()?.split('?')[0],
      type: r.initiatorType,
      duration: Math.round(r.duration),
      size: Math.round((r.transferSize || 0) / 1024),
    }))

  return {
    totalLoadTime: navTiming ? Math.round(navTiming.loadEventEnd - navTiming.startTime) : 0,
    resourceCount: resources.length,
    slowResources,
    largeResources,
  }
}

// Page load sample rate (20%)
const PAGE_LOAD_SAMPLE_RATE = 0.2

// Initialize page load reporting
function initPageLoadReporting(): void {
  if (typeof window === 'undefined') return

  // Random sampling - only report 20% of sessions
  if (Math.random() > PAGE_LOAD_SAMPLE_RATE) return

  let hasReported = false

  const doReport = (trigger: 'load' | 'fallback' | 'visibilitychange') => {
    if (hasReported) return
    hasReported = true

    const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    const loadTime = navTiming?.loadEventEnd > 0 ? navTiming.loadEventEnd - navTiming.startTime : performance.now()

    // Only report if page load is slow
    if (loadTime > THRESHOLDS.PAGE_LOAD_MS) {
      // Capture web vitals snapshot
      const lcp = getLatestLCP()
      const inp = getLatestINP()
      const cls = getLatestCLS()
      const network = getNetworkData()

      PerformanceMonitoringService.logPageLoadPerformance({
        sessionId: getSessionId(),
        userId: getUserId(),
        ...collectPageLoadReport(),
        reportTrigger: trigger,
        calculatedLoadTime: Math.round(loadTime),
        diagnostics: getClientDiagnostics(),
        locationId: getLocationId(),
        url: window.location.href,
        timestamp: new Date().toISOString(),
        // Web vitals snapshot
        lcp: lcp?.lcp,
        inp: inp?.inp,
        cls: cls?.cumulativeValue,
        effectiveNetworkType: network.effectiveType,
        networkStrength: network.strength,
        browser: network.browser,
        os: network.os,
      })
    }
  }

  // Fallback timer for stuck pages
  const fallbackTimer = setTimeout(() => doReport('fallback'), THRESHOLDS.FALLBACK_MS)

  if (document.readyState === 'complete') {
    setTimeout(() => doReport('load'), 100)
  } else {
    window.addEventListener(
      'load',
      () => {
        clearTimeout(fallbackTimer)
        setTimeout(() => doReport('load'), 100)
      },
      { once: true }
    )
  }

  // Report if user navigates away from slow page
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && performance.now() > THRESHOLDS.PAGE_LOAD_MS) {
      doReport('visibilitychange')
    }
  })
}

// ============================================================================
// Core Web Vitals & Network Monitoring - Delegated to webVitalsService
// ============================================================================
// LCP, INP, CLS, and Network monitoring are handled by the shared webVitalsService
// See: ./webVitalsService.ts

// Auto-initialize
if (typeof window !== 'undefined') {
  // Increase resource timing buffer size (default is 250, we want 500)
  try {
    performance.setResourceTimingBufferSize(500)
  } catch {
    // Silently fail if not supported
  }

  // Initialize phase tracking first
  initPhaseTracking()

  // Intercept fetch/XHR to capture call stacks
  initCallStackTracker()

  // Then start monitoring
  initResourceMonitor()
  // initPageLoadReporting()

  // Initialize Core Web Vitals (LCP, INP, CLS) and Network monitoring via shared service
  initWebVitalsService()
}
